import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink, Code, Cpu, Zap } from "lucide-react";
import { useEffect } from "react";
import SEO from "@/components/seo";

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const ApiPage = ({ embedded = false }: { embedded?: boolean }) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={embedded ? '' : 'min-h-screen'} style={{ background: PALETTE.brightSnow }}>
      <SEO
        title="API Documentation"
        description="Integrate BrandComply video compliance checks into your workflow via our API. Automate campaign brief validation and video review at scale."
        path="/api"
      />
      {!embedded && (
        <header style={{ background: PALETTE.gunmetal }} className="text-white sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div 
                className="flex items-center space-x-1 cursor-pointer"
                onClick={() => navigate('/')}
              >
                <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                  BrandComply
                </div>
              </div>

              <Button
                variant="outline"
                className="font-semibold w-fit rounded-full border-2 h-8 px-4 hover:scale-105 transition-transform transition-colors duration-200"
                style={{ borderColor: PALETTE.inchworm, color: PALETTE.inchworm, background: 'transparent' }}
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className={embedded ? '' : 'max-w-4xl mx-auto px-6 py-16'}>
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6" style={{ color: PALETTE.gunmetal }}>
            BrandComply API
          </h1>
          <p className="text-xl max-w-2xl mx-auto leading-relaxed" style={{ color: `${PALETTE.gunmetal}80` }}>
            Seamlessly integrate automated video compliance checks into your existing workflow or platform.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="p-8 rounded-2xl border-2 transition-all duration-300 hover:shadow-lg" style={{ borderColor: `${PALETTE.americanSilver}40` }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ background: `${PALETTE.inchworm}20` }}>
              <Code className="w-6 h-6" style={{ color: PALETTE.gunmetal }} />
            </div>
            <h3 className="text-xl font-bold mb-3" style={{ color: PALETTE.gunmetal }}>Flexible Integration</h3>
            <p style={{ color: `${PALETTE.gunmetal}70` }}>
              Our API allows you to trigger video checks and retrieve detailed compliance reports directly within your own environment.
            </p>
          </div>

          <div className="p-8 rounded-2xl border-2 transition-all duration-300 hover:shadow-lg" style={{ borderColor: `${PALETTE.americanSilver}40` }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ background: `${PALETTE.paleViolet}20` }}>
              <Cpu className="w-6 h-6" style={{ color: PALETTE.gunmetal }} />
            </div>
            <h3 className="text-xl font-bold mb-3" style={{ color: PALETTE.gunmetal }}>Template-Based</h3>
            <p style={{ color: `${PALETTE.gunmetal}70` }}>
              Generate unique compliance checks based on custom templates. Define specific template structures for your campaign briefs.
            </p>
          </div>

          <div className="p-8 rounded-2xl border-2 transition-all duration-300 hover:shadow-lg" style={{ borderColor: `${PALETTE.americanSilver}40` }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ background: `${PALETTE.orange}20` }}>
              <Zap className="w-6 h-6" style={{ color: PALETTE.gunmetal }} />
            </div>
            <h3 className="text-xl font-bold mb-3" style={{ color: PALETTE.gunmetal }}>Scalable & Efficient</h3>
            <p style={{ color: `${PALETTE.gunmetal}70` }}>
              Whether you're processing ten or ten thousand videos, our API scales with your needs.
            </p>
          </div>
        </div>

        <h2 className="text-3xl font-bold mb-4 text-center" style={{ color: PALETTE.gunmetal }}>
          Ready to build with BrandComply?
        </h2>
        <p className="text-lg mb-8 text-center" style={{ color: `${PALETTE.gunmetal}70` }}>
          Our API is currently in private beta. Contact us to get early access and documentation.
        </p>
        <div className="flex justify-center">
          <Button 
            onClick={() => window.location.href = 'mailto:info@brandcomply.ai'}
            className="font-bold text-lg px-10 py-6 rounded-full hover:scale-105 transition-transform duration-200 shadow-lg flex items-center justify-center gap-2 whitespace-nowrap"
            style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
          >
            <span>Talk to us!</span>
            <ExternalLink className="w-5 h-5 shrink-0" />
          </Button>
        </div>
      </main>

      {!embedded && (
        <footer className="py-12 text-white" style={{ background: PALETTE.gunmetal }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-start gap-8 md:gap-16">
              {/* Brand Info */}
              <div className="max-w-xs">
                <div 
                  className="flex items-center space-x-1 cursor-pointer"
                  onClick={() => navigate('/')}
                >
                  <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                  <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                    BrandComply
                  </div>
                </div>
                <p className="text-white/70 mt-4">
                  Launch influencer & UGC video campaigns faster and on-brand.
                </p>
              </div>

              {/* Product */}
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>
                  Product
                </h3>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    How it Works
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    Pricing
                  </button>
                  <button
                    onClick={() => navigate('/api')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    API
                  </button>
                </div>
              </div>

              {/* Legal */}
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>
                  Legal
                </h3>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/terms-of-service')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    Terms of Service
                  </button>
                  <button
                    onClick={() => navigate('/privacy-policy')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    Privacy Policy
                  </button>
                  <button
                    onClick={() => navigate('/imprint')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    Imprint
                  </button>
                </div>
              </div>
            </div>

            <div className="border-t border-white/20 mt-12 pt-8 text-center text-white/70">
              <p>&copy; 2026 BrandComply. All rights reserved.</p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default ApiPage;
